import { useMutation, useQueryClient } from '@tanstack/react-query';
import type {
  BoxItemRegroupInput,
  BoxRegroupSuccessResponse,
  ChatMessage,
  CreateRequestInput,
  PatchRequestInput,
  Request,
  RequestCarrier,
  RequestCarrierBid,
  RequestChatReadBody,
  RequestItem,
  TransitionKey,
} from '@magimp/shared';
import {
  addCarrier,
  addCarrierBid,
  addCarrierInsuranceQuote,
  deleteCarrierInsuranceQuote,
  addItem,
  createRequest,
  deleteCarrier,
  deleteCarrierBid,
  deleteItem,
  deleteRequest,
  duplicateRequest,
  markChatRead,
  markChatUnread,
  patchCarrier,
  patchCarrierBid,
  patchCarrierInsuranceQuote,
  patchItem,
  patchRequest,
  pickWinner,
  postChat,
  reconfirmCarrierInsuranceQuote,
  regroupBoxItems,
  setContainerTotals,
  transitionRequest,
  unmarkChatUnread,
  type AddCarrierBody,
  type AddCarrierInsuranceBody,
  type CreateItemBody,
  type PatchCarrierBody,
  type PatchItemBody,
  type TransitionBody,
  type UpsertBidBody,
  type UpsertCarrierInsuranceBody,
} from '../../api/requests/index.js';
import { requestsKeys } from './queryKeys.js';
import { invalidateForCreatedRequest, narrowInvalidateForRequest } from './queries.read.js';

// -- mutations --------------------------------------------------------

/**
 * @aiInvariant the single place guaranteeing every mutation's `onSuccess`
 *   invalidates the affected detail `['request', id]` and only the list
 *   queries that either contain the row or have `unread=true` filters.
 *   The `return` transition also opts into snapshot-list invalidation because
 *   it creates archive history.
 *   Never invalidates the whole `['requests']` prefix — that broad
 *   catch-up is reserved for the lifecycle/reconnect path in
 *   `invalidateRequestCaches`.
 */
function useInvalidate() {
  const qc = useQueryClient();
  return (id?: string, includeSnapshots = false) => {
    if (id) narrowInvalidateForRequest(qc, id, includeSnapshots);
  };
}

export function useCreateRequest() {
  const qc = useQueryClient();
  return useMutation<Request, Error, CreateRequestInput>({
    mutationFn: (input) => createRequest(input),
    onSuccess: (req) => invalidateForCreatedRequest(qc, req.id),
  });
}

// WHY (#2119): duplicate a China request into a new DRAFT. `clientToken` is
//   minted once per action by the caller (FR-5 single-flight); a retry
//   reuses the same token so the backend idempotent-resolve returns the
//   already-created duplicate instead of a second row. Like create, the new
//   row is absent from every list cache, so invalidate via the created-path
//   helper.
export function useDuplicateRequest() {
  const qc = useQueryClient();
  return useMutation<Request, Error, { id: string; clientToken: string }>({
    mutationFn: ({ id, clientToken }) => duplicateRequest(id, { clientToken }),
    onSuccess: (req) => invalidateForCreatedRequest(qc, req.id),
  });
}

export function usePatchRequest(id: string) {
  const invalidate = useInvalidate();
  return useMutation<Request, Error, PatchRequestInput>({
    mutationFn: (body) => patchRequest(id, body),
    onSuccess: () => invalidate(id),
  });
}

export function useDeleteRequest(id: string) {
  const invalidate = useInvalidate();
  return useMutation<void, Error, void>({
    mutationFn: () => deleteRequest(id),
    onSuccess: () => invalidate(id),
  });
}

export function useAddItem(id: string) {
  const invalidate = useInvalidate();
  return useMutation<RequestItem, Error, CreateItemBody>({
    mutationFn: (body) => addItem(id, body),
    onSuccess: () => invalidate(id),
  });
}

export function usePatchItem(id: string) {
  const invalidate = useInvalidate();
  return useMutation<RequestItem, Error, { itemId: string; body: PatchItemBody }>({
    mutationFn: ({ itemId, body }) => patchItem(id, itemId, body),
    onSuccess: () => invalidate(id),
  });
}

export function useDeleteItem(id: string) {
  const invalidate = useInvalidate();
  return useMutation<void, Error, string>({
    mutationFn: (itemId) => deleteItem(id, itemId),
    onSuccess: () => invalidate(id),
  });
}

export function useAddCarrier(id: string) {
  const invalidate = useInvalidate();
  return useMutation<RequestCarrier, Error, AddCarrierBody>({
    mutationFn: (body) => addCarrier(id, body),
    onSuccess: () => invalidate(id),
  });
}

export function usePatchCarrier(id: string) {
  const invalidate = useInvalidate();
  return useMutation<RequestCarrier, Error, { requestCarrierId: string; body: PatchCarrierBody }>({
    mutationFn: ({ requestCarrierId, body }) => patchCarrier(id, requestCarrierId, body),
    onSuccess: () => invalidate(id),
  });
}

export function useDeleteCarrier(id: string) {
  const invalidate = useInvalidate();
  return useMutation<void, Error, string>({
    mutationFn: (requestCarrierId) => deleteCarrier(id, requestCarrierId),
    onSuccess: () => invalidate(id),
  });
}

export function useAddCarrierBid(id: string) {
  const invalidate = useInvalidate();
  return useMutation<RequestCarrierBid, Error, { requestCarrierId: string; body: UpsertBidBody }>({
    mutationFn: ({ requestCarrierId, body }) => addCarrierBid(id, requestCarrierId, body),
    onSuccess: () => invalidate(id),
  });
}

export function usePatchCarrierBid(id: string) {
  const invalidate = useInvalidate();
  return useMutation<
    RequestCarrierBid,
    Error,
    { requestCarrierId: string; bidId: string; body: UpsertBidBody }
  >({
    mutationFn: ({ requestCarrierId, bidId, body }) =>
      patchCarrierBid(id, requestCarrierId, bidId, body),
    onSuccess: () => invalidate(id),
  });
}

export function useDeleteCarrierBid(id: string) {
  const invalidate = useInvalidate();
  return useMutation<void, Error, { requestCarrierId: string; bidId: string }>({
    mutationFn: ({ requestCarrierId, bidId }) => deleteCarrierBid(id, requestCarrierId, bidId),
    onSuccess: () => invalidate(id),
  });
}

export function usePickWinner(id: string) {
  const invalidate = useInvalidate();
  return useMutation<void, Error, string>({
    mutationFn: (bidId) => pickWinner(id, bidId),
    onSuccess: () => invalidate(id),
  });
}

// WHY (#1163): create-only hook — adds one row (primary or override
//   based on `body.scope`). Update/reconfirm/delete are addressed by
//   `quoteId` via separate hooks below. The pre-#1163 combined
//   upsert hook was removed because `(scope, scopeRefId)` are
//   create-only — a PATCH cannot retarget a row.
export function useAddCarrierInsuranceQuote(id: string) {
  const invalidate = useInvalidate();
  return useMutation<unknown, Error, { requestCarrierId: string; body: AddCarrierInsuranceBody }>({
    mutationFn: ({ requestCarrierId, body }) =>
      addCarrierInsuranceQuote(id, requestCarrierId, body),
    onSuccess: () => invalidate(id),
  });
}

export function usePatchCarrierInsuranceQuote(id: string) {
  const invalidate = useInvalidate();
  return useMutation<
    unknown,
    Error,
    { requestCarrierId: string; quoteId: string; body: UpsertCarrierInsuranceBody }
  >({
    mutationFn: ({ requestCarrierId, quoteId, body }) =>
      patchCarrierInsuranceQuote(id, requestCarrierId, quoteId, body),
    onSuccess: () => invalidate(id),
  });
}

export function useReconfirmCarrierInsuranceQuote(id: string) {
  const invalidate = useInvalidate();
  return useMutation<unknown, Error, { requestCarrierId: string; quoteId: string }>({
    mutationFn: ({ requestCarrierId, quoteId }) =>
      reconfirmCarrierInsuranceQuote(id, requestCarrierId, quoteId),
    onSuccess: () => invalidate(id),
  });
}

export function useDeleteCarrierInsuranceQuote(id: string) {
  const invalidate = useInvalidate();
  return useMutation<void, Error, { requestCarrierId: string; quoteId: string }>({
    mutationFn: ({ requestCarrierId, quoteId }) =>
      deleteCarrierInsuranceQuote(id, requestCarrierId, quoteId),
    onSuccess: () => invalidate(id),
  });
}

export function usePostChat(id: string) {
  const qc = useQueryClient();
  const invalidate = useInvalidate();
  return useMutation<ChatMessage, Error, string>({
    mutationFn: (text) => postChat(id, text),
    // WHY: cancel any in-flight chat-list reads under this request's chat
    // prefix so the server's authoritative response — which now includes
    // this newly-posted message — replaces the cached page on the next
    // refetch. Invalidating the prefix matches every chat-list variant
    // (`useChatList(id, query)` keys are `[...requestsKeys.chat(id), query]`),
    // so a non-default `{limit: 50}` consumer is refreshed alongside the
    // default `{}` shape. We do not append optimistically: the typed line
    // arrives within one network round-trip, and the prior implementation's
    // snapshot/rollback path was inert (it never wrote to the cache).
    onMutate: async () => {
      await qc.cancelQueries({ queryKey: requestsKeys.chat(id) });
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: requestsKeys.chat(id) });
      invalidate(id);
    },
  });
}

/**
 * @aiPurpose advance the actor's read pointer for the chat of request
 *   `id` to the chronologically newest visible message (#570). The
 *   server runs a DB-side `GREATEST(stored, messageCreatedAt)` upsert,
 *   so the boolean check-mark surface re-renders without the client
 *   guessing which message id is "latest".
 * @aiInvariant invalidates BOTH the chat slice (so own-message
 *   `readByCounterparty` flips for the counterparty's view via the
 *   realtime fan-out) and the unread-summary prefix (so the row's
 *   badge updates).
 */
export function useMarkChatRead(id: string) {
  const qc = useQueryClient();
  return useMutation<void, Error, RequestChatReadBody>({
    mutationFn: (body) => markChatRead(id, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: requestsKeys.chat(id) });
      void qc.invalidateQueries({ queryKey: requestsKeys.chatUnreadSummaryPrefix() });
    },
  });
}

export function useMarkChatUnread(id: string) {
  const qc = useQueryClient();
  return useMutation<void, Error, void>({
    mutationFn: () => markChatUnread(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: requestsKeys.chatUnreadSummaryPrefix() });
    },
  });
}

export function useUnmarkChatUnread(id: string) {
  const qc = useQueryClient();
  return useMutation<void, Error, void>({
    mutationFn: () => unmarkChatUnread(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: requestsKeys.chatUnreadSummaryPrefix() });
    },
  });
}

export interface RegroupBoxItemsArgs {
  boxId: string;
  ifMatch: string;
  items: ReadonlyArray<BoxItemRegroupInput>;
}

/**
 * @aiPurpose narrow PATCH mutation for the #1178 China-side regroup
 *   endpoint. The `If-Match` precondition is a raw ISO from
 *   `RequestBox.updatedAt` (D7).
 * @aiInvariant on success rebaselines the affected box's `updatedAt`
 *   in-place via `setQueryData` (AC15 — strictly newer ISO) so the
 *   next save can attach the new If-Match without an extra GET
 *   round-trip. The cross-tab manager view picks up the regrouped
 *   ordinals from the existing `requests:updated` realtime emit
 *   (AC11). 412 / 409 leave the cache intact so the caller can render
 *   the conflict toast against the token they sent.
 */
export function useRegroupBoxItems(id: string) {
  const qc = useQueryClient();
  return useMutation<BoxRegroupSuccessResponse, Error, RegroupBoxItemsArgs>({
    mutationFn: ({ boxId, ifMatch, items }) => regroupBoxItems(id, boxId, ifMatch, items),
    onSuccess: (resp, vars) => {
      // Local rebaseline (AC15): bump the affected box's `updatedAt` so
      //   the next save attaches the new If-Match, AND overlay the staged
      //   item ordinals onto the cached items so the read table reflects
      //   the saved state without waiting for realtime. The cached items
      //   carry the *pre-mutation* ordinals — the wire body we just sent
      //   is the only source of truth for the new ordinals.
      qc.setQueryData<Request>(requestsKeys.detail(id), (curr) => {
        if (!curr) return curr;
        type CachedBoxItem = { id: string; groupOrdinal?: number } & Record<string, unknown>;
        type CachedBox = {
          id: string;
          updatedAt?: string;
          items?: ReadonlyArray<CachedBoxItem>;
        } & Record<string, unknown>;
        const wire = curr as Request & { boxes?: ReadonlyArray<CachedBox> };
        if (!wire.boxes) return curr;
        const nextOrdinalByItemId = new Map(vars.items.map((it) => [it.itemId, it.groupOrdinal]));
        return {
          ...wire,
          boxes: wire.boxes.map((b) => {
            if (b.id !== vars.boxId) return b;
            const nextItems = b.items?.map((it) => {
              const next = nextOrdinalByItemId.get(it.id);
              return next === undefined ? it : { ...it, groupOrdinal: next };
            });
            return { ...b, updatedAt: resp.updatedAt, ...(nextItems ? { items: nextItems } : {}) };
          }),
        } as Request;
      });
    },
  });
}

export function useTransition(id: string) {
  const invalidate = useInvalidate();
  return useMutation<Request, Error, { key: TransitionKey; body?: TransitionBody }>({
    mutationFn: ({ key, body }) => transitionRequest(id, key, body),
    onSuccess: (_request, { key }) => invalidate(id, key === 'return'),
  });
}

// WHY (#2175): set/correct CONTAINER request-level weight/volume on a
//   post-send row. Mirrors usePatchItem — invalidates the detail cache
//   on success so the wire aggregate reflects the new stored value.
export function useSetContainerTotals(id: string) {
  const invalidate = useInvalidate();
  return useMutation<Request, Error, { weight: number; volume: number }>({
    mutationFn: (body) => setContainerTotals(id, body),
    onSuccess: () => invalidate(id),
  });
}
